import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig, loginRequest } from '../config/msalConfig';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import twoFactorService from '../services/twoFactorService';
import { doc, getDoc} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useMSALLogin } from '../hooks/useMSALLogin';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const msalInstanceRef = useRef(new PublicClientApplication(msalConfig));
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authProvider, setAuthProvider] = useState(null); // 'firebase' or 'msal'
  const { handleMSALLoginSuccess } = useMSALLogin();

  useEffect(() => {
    msalInstanceRef.current.initialize().then(() => {
      setMsalInitialized(true);
    });

    // MSAL event callback
    const msalCallbackId = msalInstanceRef.current.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        handleMSALLoginSuccess(event.payload).then(result => {
          if (result.success) {
            setCurrentUser(result.user);
            setAuthProvider('msal');
          }
        });
      }
    });

    // Firebase auth state listener
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Get user document from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setCurrentUser({ ...user, ...userDoc.data() });
        setAuthProvider('firebase');
      } else {
        // Check for MSAL account if no Firebase user
        const activeAccount = msalInstanceRef.current.getActiveAccount();
        if (activeAccount) {
          const result = await handleMSALLoginSuccess({ account: activeAccount });
          if (!result.success) {
            setCurrentUser(null);
            setAuthProvider(null);
          }
        } else {
          setCurrentUser(null);
          setAuthProvider(null);
        }
      }
      setLoading(false);
    });

    return () => {
      if (msalCallbackId) {
        msalInstanceRef.current.removeEventCallback(msalCallbackId);
      }
      unsubscribe();
    };
  }, []);

  // Email/Password login with Firebase
  async function loginWithEmail(email, password) {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Microsoft login with MSAL
  async function loginWithMicrosoft() {
    try {
      setError(null);
      if (!msalInitialized) {
        await msalInstanceRef.current.initialize();
        setMsalInitialized(true);
      }

      const response = await msalInstanceRef.current.loginPopup(loginRequest);
      
      const loginResult = await handleMSALLoginSuccess(response);
      
      if (loginResult?.error) {
        return { error: loginResult.error };
      }
      
      return { success: true, account: response.account };
    } catch (err) {
      console.error('Error in loginWithMicrosoft:', {
        name: err.name,
        message: err.message,
        errorCode: err.errorCode,
        stack: err.stack,
        innerError: err.innerError
      });
      const errorMessage = err.message || 'An error occurred during login';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }

  // Verify 2FA
  async function verify2FA(otp) {
    try {
      setError(null);
      const isValid = await twoFactorService.verifyOTP(currentUser.uid, otp);
      if (isValid.status) {
        return true;
      }
      return false;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Logout
  async function logout() {
    try {
      setError(null);
      if (authProvider === 'firebase') {
        await signOut(auth);
      } else if (authProvider === 'msal') {
        await msalInstanceRef.current.logoutPopup();
      }
      setCurrentUser(null);
      setAuthProvider(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Get access token (for MSAL)
  async function getAccessToken() {
    try {
      if (authProvider !== 'msal') {
        throw new Error('Not authenticated with MSAL');
      }
      const account = msalInstanceRef.current.getActiveAccount();
      if (!account) {
        throw new Error('No active account');
      }
      const response = await msalInstanceRef.current.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      return response.accessToken;
    } catch (err) {
      console.error('Error getting access token:', err);
      throw err;
    }
  }

  const value = {
    currentUser,
    loading,
    error,
    authProvider,
    loginWithEmail,
    loginWithMicrosoft,
    verify2FA,
    logout,
    getAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}